import { baseApi, API_TAGS, createCacheTags, Params, BaseQueryParams } from '@/shared/api';
import {
  MappingLocationsResponse,
  MappingDataResponse,
  MappingCorridorsResponse,
  MappingBreakdownResponse,
  MappingConsignmentsResponse,
  createDashboardEndpointBuilder
} from '@/entities/dashboard';

import {
  CORRIDOR_TYPES,
  BREAKDOWN_TYPES,
  REQUEST_TYPES,
  type CorridorType,
  type BreakdownType,
  type ConsignmentListType,
  type RequestType
} from './constants';

const mappingEndpoint = createDashboardEndpointBuilder('/mapping', '/api/v2/dashboard');

const createMappingTags = (params?: Params) => [
  API_TAGS.DASHBOARD,
  API_TAGS.MAPPING,
  ...createCacheTags({ baseTag: API_TAGS.MAPPING, params })
];

interface WeekParams extends BaseQueryParams {
  weekNo?: number;
  organizationId?: string;
  /** Partial, case-insensitive match on the consignment (CMR) number. */
  consignmentNumber?: string;
}

interface CorridorParams extends WeekParams {
  type?: CorridorType;
  /** Comma-separated category ids — the map's picker is a multi-select. */
  vehicleCategoryIds?: string;
}

interface RequestParams extends WeekParams {
  type?: RequestType;
}

interface BreakdownParams extends BaseQueryParams {
  /** Every location behind the marker — one id for a plain marker, many for a cluster. */
  locationIds: string[];
  type?: BreakdownType;
  organizationId?: string;
  consignmentNumber?: string;
}

interface ConsignmentsParams extends BaseQueryParams {
  locationIds: string[];
  type: ConsignmentListType;
  weekNo?: number;
  organizationId?: string;
  consignmentNumber?: string;
}

export const mappingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMappingLocations: builder.query<MappingLocationsResponse, BaseQueryParams | void>({
      query: (params) => ({
        url: mappingEndpoint('/locations'),
        params: params || undefined
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'locations',
          projectId: arg?.projectId,
          siteId: arg?.siteId
        })
    }),

    getMappingStock: builder.query<MappingDataResponse, WeekParams | undefined>({
      query: (params) => ({
        url: mappingEndpoint('/stock'),
        params
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'stock',
          projectId: arg?.projectId,
          siteId: arg?.siteId,
          weekNo: arg?.weekNo,
          organizationId: arg?.organizationId,
          consignmentNumber: arg?.consignmentNumber
        })
    }),

    getMappingCorridors: builder.query<MappingCorridorsResponse, CorridorParams | undefined>({
      query: (params = { type: CORRIDOR_TYPES.IN_TRANSIT }) => ({
        url: mappingEndpoint('/corridors'),
        params
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'corridors',
          projectId: arg?.projectId,
          siteId: arg?.siteId,
          weekNo: arg?.weekNo,
          type: arg?.type,
          organizationId: arg?.organizationId,
          consignmentNumber: arg?.consignmentNumber,
          vehicleCategoryIds: arg?.vehicleCategoryIds
        })
    }),

    getMappingReleases: builder.query<MappingDataResponse, WeekParams | undefined>({
      query: (params) => ({
        url: mappingEndpoint('/releases'),
        params
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'releases',
          projectId: arg?.projectId,
          siteId: arg?.siteId,
          weekNo: arg?.weekNo,
          organizationId: arg?.organizationId,
          consignmentNumber: arg?.consignmentNumber
        })
    }),

    getMappingRequests: builder.query<MappingDataResponse, RequestParams | undefined>({
      query: (params = { type: REQUEST_TYPES.CONSIGNMENTS }) => ({
        url: mappingEndpoint('/requests'),
        params
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'requests',
          projectId: arg?.projectId,
          siteId: arg?.siteId,
          weekNo: arg?.weekNo,
          type: arg?.type,
          organizationId: arg?.organizationId,
          consignmentNumber: arg?.consignmentNumber
        })
    }),

    getMappingBreakdown: builder.query<MappingBreakdownResponse, BreakdownParams>({
      query: ({
        locationIds,
        type = BREAKDOWN_TYPES.BY_ORGANIZATION,
        projectId,
        siteId,
        organizationId,
        consignmentNumber
      }) => ({
        url: mappingEndpoint('/breakdown'),
        params: {
          locationIds: locationIds.join(','),
          type,
          projectId,
          siteId,
          organizationId,
          consignmentNumber
        }
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'breakdown',
          projectId: arg.projectId,
          siteId: arg.siteId,
          locationIds: arg.locationIds.join(','),
          type: arg.type || BREAKDOWN_TYPES.BY_ORGANIZATION,
          organizationId: arg.organizationId,
          consignmentNumber: arg.consignmentNumber
        })
    }),

    getMappingConsignments: builder.query<MappingConsignmentsResponse, ConsignmentsParams>({
      query: ({ locationIds, type, weekNo, projectId, siteId, organizationId, consignmentNumber }) => ({
        url: mappingEndpoint('/consignments'),
        params: {
          locationIds: locationIds.join(','),
          type,
          weekNo,
          projectId,
          siteId,
          organizationId,
          consignmentNumber
        }
      }),
      providesTags: (_result, _error, arg) =>
        createMappingTags({
          endpointName: 'consignments',
          projectId: arg.projectId,
          siteId: arg.siteId,
          weekNo: arg.weekNo,
          locationIds: arg.locationIds.join(','),
          type: arg.type,
          organizationId: arg.organizationId,
          consignmentNumber: arg.consignmentNumber
        })
    })
  })
});

export const {
  useGetMappingLocationsQuery,
  useGetMappingStockQuery,
  useGetMappingCorridorsQuery,
  useGetMappingReleasesQuery,
  useGetMappingRequestsQuery,
  useGetMappingBreakdownQuery,
  useGetMappingConsignmentsQuery
} = mappingApi;
